import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Optional
} from '@angular/core';
import { CanColor, mixinColor, MixinElementRefBase, ThemePalette } from '@ngx-simple/core/common-behaviors';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { SafeAny } from '@ngx-simple/core/types';
import { Subject, Subscription } from 'rxjs';
import { CalendarUserEvent } from './calendar-body.component';
import { SimCalendarComponent } from './calendar.component';
import { SimDateRangeSelectionStrategy, SIM_DATE_RANGE_SELECTION_STRATEGY } from './date-range-selection-strategy';
import { DateRange, SimDateSelectionModel } from './date-selection-model';
import { SimDatepickerBase } from './datepicker-base';

const _SimDatepickerContentMixinBase = mixinColor(MixinElementRefBase);

@Directive()
export abstract class SimDatepickerContentBase<S, D> extends _SimDatepickerContentMixinBase implements AfterViewInit, OnDestroy, CanColor {
  @Input() color: ThemePalette;

  _animationDone = new Subject<void>();

  @HostBinding('@transformPanel')
  _animationState: 'enter' | 'void' = 'enter';

  /** 对创建浮层的datePicker的引用 */
  datepicker: SimDatepickerBase<SafeAny, S, D>;

  /** 比较范围的开始 */
  comparisonStart: D | null;

  /** 比较范围的结束 */
  comparisonEnd: D | null;

  protected _calendar: SimCalendarComponent<D>;

  private oldData: D;

  private _subscriptions = new Subscription();

  constructor(
    elementRef: ElementRef<HTMLElement>,
    public _dateAdapter: SimDateAdapter<D>,
    protected _changeDetectorRef: ChangeDetectorRef,
    protected _model: SimDateSelectionModel<S, D>,
    @Optional()
    @Inject(SIM_DATE_RANGE_SELECTION_STRATEGY)
    protected _rangeSelectionStrategy?: SimDateRangeSelectionStrategy<D>
  ) {
    super(elementRef);
  }

  ngAfterViewInit(): void {
    this._subscriptions.add(
      this.datepicker._stateChanges.subscribe(() => {
        this._changeDetectorRef.markForCheck();
      })
    );
    this._calendar.focusActiveCell();
    this.oldData = this.datepicker.startAt;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._animationDone.complete();
    this._animationDone.subscribe();
  }

  @HostListener('@transformPanel.done')
  _endExitAnimation() {
    this._animationDone.next();
  }

  _startExitAnimation() {
    this._animationState = 'void';

    if (this._changeDetectorRef) {
      this._changeDetectorRef.markForCheck();
    }
  }

  /** 选择给定的日期 */
  select(date: D): void {
    this._model.add(date);
  }

  selectedClicked(affirm: boolean) {
    if (affirm) {
      this.datepicker.close();
    } else {
      this._model.add(this.oldData);
      this.datepicker.close();
    }
  }

  /** 切换日期和时间选择view */
  toggleView() {
    this.datepicker.startView = this.datepicker.startView === 'month' ? 'date' : 'month';
    this._changeDetectorRef.markForCheck();
  }

  /**
   * 如果是 single 返回是当天点击时间
   * 如果是 range 返回是当前00:00:00 - 23:59:59
   */
  toggleToday() {
    this._handleUserSelection({
      value: this._dateAdapter.today(),
      event: null
    });
  }

  _handleUserSelection(event: CalendarUserEvent<D | null>, range?: 'start' | 'end'): void {
    console.log('_handleUserSelection');
    if (this._model && this._dateAdapter) {
      const selection = this._model.selection;
      const value = event.value;
      const isRange = selection instanceof DateRange;
      if (isRange && this._rangeSelectionStrategy) {
        const currentRange = (selection as unknown) as DateRange<D>;
        // 如果选择开始区间，强制把结束设为null
        // if (range === 'start' && !currentRange.end) {
        //   currentRange = new DateRange(currentRange.start, null);
        // } else {
        //   if (range === 'start') {
        //     currentRange = new DateRange(null, currentRange.end);
        //   } else {
        //     currentRange = new DateRange(currentRange.start, null);
        //   }
        // }
        const newSelection = this._rangeSelectionStrategy.selectionFinished(value, currentRange, event.event);
        console.log('_handleUserSelection', range, currentRange, value, newSelection);
        this._model.updateSelection((newSelection as unknown) as S, this);
      } else if (value && (isRange || !this._dateAdapter.sameDate(value, (selection as unknown) as D))) {
        this._model.add(value);
      }
    }
    if (!this._model || this._model.isComplete()) {
      this.datepicker.close();
    }
  }

  abstract _getSelected(): D | DateRange<D> | null;
}
